import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle } from "react";
import { useIsOverflow } from "utilities/hookutils";
import { SidePanelContext } from "./SidePanelContext";
import classNames from "classnames";
import { ButtonText } from "components/Atoms/Button";

interface SidePanelHeaderProps {
  title: string
}

export const SidePanelHeader = (props: React.PropsWithChildren<SidePanelHeaderProps>) => {
  const { title, children } = props;
  const { closePanel } = useContext(SidePanelContext);

  return <>
    <div className="side-panel-header">
      <div className="nav-text-regular">{title}</div>
      <div className="grow1">
        {children}
      </div>
      <div className="align-start">
        <PhosphorXButton
          onClick={() => { closePanel(); }}
          layout="in-circle"
        />
      </div>
    </div>
  </>;
};

export const SidePanelFooter = (props: React.PropsWithChildren<unknown>) => {
  const { children } = props;
  const { hasBack, back } = useContext(SidePanelContext);

  return <div className="side-panel-footer">
    {hasBack && <div style={{ marginRight: "auto" }}>
      <ButtonText
        label={RESX.Buttons.btnBack}
        onClick={() => back()}
      />
    </div>}
    {children}
  </div >;
};

interface SidePanelContentProps {
  className?: string
}

export const SidePanelContent = (props: React.PropsWithChildren<SidePanelContentProps>) => {
  const { children, className } = props;
  const { panel } = useContext(SidePanelContext);
  const ref = React.createRef<HTMLDivElement>();
  const isOverflow = useIsOverflow(ref, panel?.id);
  return <div
    style={{ paddingRight: isOverflow ? `${12}px` : undefined }}
    ref={ref}
    className={classNames("side-panel-content", className)}
  >
    {children}
  </div>;
};

export const SidePanelVerticalDivider = () => <div className="side-panel-vertical-divider" />;

type SidePanelSize = "medium" | "large";

export interface SidePanelBaseProps {
  size: SidePanelSize
  trigger: JSX.Element
  id: string
  onOpen?: () => void
}

const getWidthFromSize = (size: SidePanelSize) => {
  switch (size) {
    case "medium":
      return 450;
    case "large":
      return 800;
  }
};

export interface SidePanelRef {
  open: () => void
}

export const SidePanelBase = forwardRef((props: React.PropsWithChildren<SidePanelBaseProps>, ref) => {
  const { trigger, children, size, id, onOpen } = props;
  const width = getWidthFromSize(size);
  const { panel, updatePanel, openPanel } = useContext(SidePanelContext);

  const isOpen = panel?.id === id;

  useImperativeHandle(ref, () => ({
    open: onTriggerClick
  }));

  useEffect(() => {
    if (isOpen) {
      updatePanel(<div style={{ minWidth: `${width}px` }} className="side-panel" key={id}>
        {children}
      </div>);
    }
  }, [children, isOpen, id]);

  const onTriggerClick = () => {
    if (isOpen) {
      return;
    }

    openPanel({
      jsx: <div style={{ minWidth: `${width}px` }} className="side-panel" key={id}>
        {children}
      </div>,
      id,
      width
    });
    if (onOpen) {
      onOpen();
    }
  };

  return <span className={isOpen ? "side-panel-open" : undefined} style={{ display: "contents" }} role="button" onClick={onTriggerClick}>
    {trigger}
  </span>;
});

export const useOpenSidePanel = () => {
  const { openPanel } = useContext(SidePanelContext);

  const openSidePanel = useCallback((panel: React.ReactNode, id: string, size: SidePanelSize = "medium") => {
    const width = getWidthFromSize(size);
    openPanel({
      jsx: <div style={{ minWidth: `${width}px` }} className="side-panel" key={id}>
        {panel}
      </div>,
      id,
      width
    });
  }, [openPanel]);

  return openSidePanel;
}
